import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import * as Sentry from "@sentry/vue";
import { ApiPlugin } from "@/packages/api/vue";
import "./styles/app.css"; // Import CSS for application.
import { AUTH0_CLIENT_ID, AUTH0_DOMAIN, PUBLIC_DSN } from "./environment";
import { Auth0 } from "@/packages/common/auth";
import { client, fetchClient } from "./lib/api";
import { createPinia } from "pinia";
import { configureAffinityErrorReporter } from "@/lib/affinityErrorReporter";

const rootElement: HTMLElement = document.getElementById("app") as HTMLElement;

const pinia = createPinia();
const app = createApp(App);

app.use(pinia);

if (PUBLIC_DSN) {
  Sentry.init({
    app,
    dsn: PUBLIC_DSN,
    sendDefaultPii: true,
    attachStacktrace: true,
    integrations: [Sentry.browserTracingIntegration({ router })],
    tracesSampleRate: 0.0,
    ignoreErrors: [
      "CanceledError",
      /Request aborted/,
      /Network Error/,
      /cdn.chat.appfire.app/,
      /Unable to preload CSS/,
    ],
  });
}

const auth = new Auth0({
  domain: AUTH0_DOMAIN || "",
  clientId: AUTH0_CLIENT_ID || "",

  /*
   * Disabled because localStorage persistence causes stale user sessions.
   * When the API token expires, the client still perceives the user as authenticated based on cached profile data.
   */
  //  cacheLocation: "localstorage",

  authorizationParams: {
    redirect_uri: window.location.origin,
  },
  onRedirectCallback: (appState) => {
    router.push(
      appState && appState.targetUrl
        ? appState.targetUrl
        : window.location.pathname,
    );
  },
});

app.config.globalProperties.$auth = auth;

configureAffinityErrorReporter({
  isAuthenticated: () => auth.isAuthenticated(),
  getUserId: () => {
    const user = auth.getUser();

    if (typeof user?.sub === "string") {
      return user.sub;
    }

    if (typeof user?.user_id === "string") {
      return user.user_id;
    }

    if (typeof user?.id === "string") {
      return user.id;
    }

    return null;
  },
});

app.config.globalProperties.$api = client;

app.config.globalProperties.$whitelabel = {
  company: "Simply Broadband",
  parentCompany: "Simply Broadband Ltd",
  supportEmail: "support@simplybroadband.co.nz",
  supportPhone: "09 281 0356",
  supportPhoneE164: "+6492810356",
  url: "https://simplybroadband.co.nz",
};

app.use(router);
app.use(ApiPlugin, {
  client: fetchClient,
});

// Mount Vue application
app.mount(rootElement);
